// External Libraries
import * as React from "react";
import { formatText, formatDateString } from "bernie-l10n";

// Types
import { Hotel } from "typings/microserviceModels/hotels-flex-module";

// UITK
import { UitkLayoutFlex, UitkLayoutFlexItem } from "uitk-react-layout-flex";
import { UitkSpacing } from "uitk-react-spacing";
import { UitkText } from "uitk-react-text";

// Components
import { ReviewCountAndRating } from "components/flexComponents/Hotels/components/ReviewCountAndRating";
import { showReviewCountAndRating } from "components/flexComponents/Hotels/components/FeaturedReviewContent";
import { useHotelsContext } from "components/flexComponents/Hotels/components/HotelsContext";
import { HotelName, HotelRating } from "components/flexComponents/Hotels/components/HotelNameRatingAndAddress";
import DistanceOrRating from "components/flexComponents/Hotels/views/HotelsViews/components/shared/DistanceOrRating";

// Utils
import { getAddress } from "components/flexComponents/Hotels/utils/getAddress";
import { ratingIcon } from "components/utility/RatingUtil";
import PriceLockup from "../../views/HotelsViews/components/shared/PriceLockup";
import { HotelPriceLockupWithDates } from "../../utils/A11y/HotelPriceLockupWithDates";
import { getHotelLink } from "../HotelCard";

export interface SummarizedContentViewProps {
  showPrice?: boolean;
  showAddress?: boolean;
  showNeighborhood?: boolean;
  showDistance?: boolean;
  showRating?: boolean;
}

interface SummarizedContentProps extends SummarizedContentViewProps {
  hotel: Hotel;
}

export const SummarizedContent: React.FC<SummarizedContentProps> = ({
  hotel,
  showPrice = true,
  showAddress = true,
  showNeighborhood = false,
  showDistance = false,
  showRating = false,
}) => {
  const {
    hotelName,
    reviewCount,
    hotelOverallReviewRating,
    featuredReviews,
    hotelStarRating,
    neighborhoodName,
    scoreDenominator,
  } = hotel;
  const { context, model } = useHotelsContext();

  const language = context.locale?.split(/[_-]/);
  const hotelRatingIcon = ratingIcon(context.locale, context.site?.id);

  return (
    <UitkLayoutFlex direction="column" justifyContent="space-between">
      <UitkLayoutFlex justifyContent="space-between">
        <UitkLayoutFlexItem shrink={1}>
          <UitkSpacing margin={{ inlineend: "one" }}>
            <div>
              <UitkSpacing margin={{ blockend: "one" }}>
                <div>
                  <HotelName tag="h3" overflow="truncate-2-lines" hotelName={hotelName} />
                  {showRating && (
                    <UitkSpacing margin={{ blockend: "one" }}>
                      <HotelRating hotelStarRating={hotelStarRating} hotelRatingIcon={hotelRatingIcon} />
                    </UitkSpacing>
                  )}
                </div>
              </UitkSpacing>
              {showAddress && (
                <UitkSpacing padding={{ blockend: "one" }}>
                  <UitkText size={300}>{getAddress(hotel)}</UitkText>
                </UitkSpacing>
              )}
              {showNeighborhood && neighborhoodName && (
                <UitkLayoutFlex justifyContent="start">
                  <UitkLayoutFlexItem>
                    <UitkSpacing padding={{ blockend: "one" }} margin={{ inlineend: "one" }}>
                      <UitkText>{neighborhoodName}</UitkText>
                    </UitkSpacing>
                  </UitkLayoutFlexItem>
                  {showDistance && (
                    <>
                      <UitkLayoutFlexItem>
                        <span>&#8208;</span>
                      </UitkLayoutFlexItem>
                      <UitkLayoutFlexItem>
                        <DistanceOrRating model={model} hotel={hotel} context={context} showIcon={false} />
                      </UitkLayoutFlexItem>
                    </>
                  )}
                </UitkLayoutFlex>
              )}
            </div>
          </UitkSpacing>
        </UitkLayoutFlexItem>
        {showPrice && (
          <UitkLayoutFlexItem shrink={0}>
            <div>
              <PriceLockup
                hotel={hotel}
                hotelLink={getHotelLink(hotel, model.destinationId, context)}
                priceA11y={new HotelPriceLockupWithDates(hotel, formatText, formatDateString)}
                hasGetRatesText
              />
            </div>
          </UitkLayoutFlexItem>
        )}
      </UitkLayoutFlex>
      {showReviewCountAndRating(model.showGuestRating, reviewCount, hotelOverallReviewRating, featuredReviews) && (
        <ReviewCountAndRating
          hotelOverallReviewRating={hotelOverallReviewRating}
          reviewCount={reviewCount}
          language={language}
          scoreDenominator={scoreDenominator}
        />
      )}
    </UitkLayoutFlex>
  );
};
